import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

interface FieldWrapperProps {
  fieldId: string;
  /** Whether this field is currently highlighted (e.g. via :questionId param). */
  isActive?: boolean;
  /** Whether this is an even-indexed row (0-based) within its group. */
  isEven?: boolean;
  /** Click handler -- navigates to question detail drill-down. */
  onClick?: () => void;
  children: ReactNode;
}

/**
 * Common layout wrapper for value fields (checkpoint, numeric, task).
 * Visual: pl-8 indent, alternating bg-white / bg-muted/20,
 * active highlight with border-l-2 border-l-primary, cursor-pointer.
 *
 * Parent group uses divide-y divide-border to separate rows.
 */
export function FieldWrapper({
  fieldId,
  isActive,
  isEven,
  onClick,
  children,
}: FieldWrapperProps) {
  return (
    <div
      id={`field-${fieldId}`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={cn(
        'flex items-center gap-2 px-3 py-2.5 pl-8',
        'cursor-pointer transition-colors',
        // Active state takes priority
        isActive && 'bg-primary/5 border-l-primary border-l-2',
        // Alternating row backgrounds for readability
        !isActive && isEven && 'bg-card',
        !isActive &&
          !isEven &&
          'bg-foreground/[0.03] dark:bg-foreground/[0.04]',
        !isActive && 'hover:bg-foreground/[0.06]',
      )}
    >
      {children}
    </div>
  );
}
